import { z } from 'zod'

// --- String literal enums ---

export const cornerStatusSchema = z.enum([
  'waiting', 'confirmed', 'refused', 'expects',
  'cooking', 'ready', 'issued_courier', 'received', 'waiting_on_counter',
])

export const eatPlaceSchema = z.enum([
  'car', 'market_table', 'market_corner', 'togo_corner',
  'togo_courier', 'togo_market', 'togo_delivery', 'togo_yandex',
  'togo_yandex_pickup', 'togo_sber', 'consumer', 'togo_magnit', 'togo_vkusvill',
])

export const orderStatusSchema = z.enum([
  'waiting', 'confirmed', 'refused', 'expects',
  'cooking', 'ready', 'issued_courier', 'waiting_on_counter', 'received',
])

// --- Nested decoders ---

const robotHandoverItemDecoder = z.object({
  type: z.string().nullable().default(null),
  status: z.string().nullable().default(null),
})

const routePointsSnapshotDecoder = z.object({
  handover: z.object({
    items: z.array(robotHandoverItemDecoder).default([]),
  }).nullable().default(null),
}).nullable().default(null)

export const externalDeliveryDecoder = z.object({
  pickupVisitStatus: z.string().nullable().default(null),
  dropoffVisitStatus: z.string().nullable().default(null),
  readyForPickup: z.boolean().default(false),
  pickupCompleted: z.boolean().default(false),
  status: z.string().nullable().default(null),
  courierName: z.string().nullable().default(null),
  routePointsSnapshot: routePointsSnapshotDecoder,
})

export const outletShortDecoder = z.object({
  id: z.number(),
  title: z.string(),
  image: z.string().nullable(),
})

export const optionDecoder = z.object({
  id: z.number(),
  title: z.string(),
  price: z.number(),
})

export const orderItemDecoder = z.object({
  id: z.number(),
  order_id: z.number(),
  outlet_id: z.number(),
  qty_item: z.number(),
  price_item: z.number(),
  discount_percent: z.number(),
  full_price: z.number(),
  title: z.string(),
  image: z.string(),
  weight: z.string(),
  options: z.array(optionDecoder),
  measure: z.string(),
  note: z.string().nullable(),
  units: z.string(),
  is_weight: z.number(),
})

export const outletDecoder = z.object({
  id: z.number(),
  title: z.string(),
  work_hours_start: z.string(),
  work_hours_end: z.string(),
  support_phone: z.string(),
  image: z.string().nullable(),
  market_title: z.string(),
  is_available_for_orders: z.boolean(),
})

export const outletOrderLazyDecoder = z.object({
  id: z.number(),
  order_id: z.number(),
  status: cornerStatusSchema,
  title: z.string(),
  isLoading: z.boolean().default(false),
})

export const outletOrderDecoder = z.object({
  id: z.number(),
  order_id: z.number(),
  outlet_id: z.number(),
  image: z.string().nullable(),
  status: cornerStatusSchema,
  order_items_full_sum: z.number(),
  order_items: z.array(orderItemDecoder),
  outlet: outletShortDecoder,
  isLoading: z.boolean().default(false),
})

export const orderLazyDecoder = z.object({
  address: z.string().default('нет адреса'),
  id: z.number(),
  created_at: z.number(),
  full_cost: z.number(),
  delivery_cost: z.number(),
  distanceFromMarketToCustomer: z.number().nullable(),
  name: z.string().nullable(),
  phone: z.string(),
  eat_place: eatPlaceSchema,
  eats_id: z.string().nullable(),
  ready_time: z.number(),
  ready_quickly: z.number(),
  note: z.string().nullable(),
  table: z.string().nullable(),
  ready_status: orderStatusSchema.default('waiting'),
  car_number: z.string().nullable(),
  yandexDeliveryType: z.string().nullable().default(null),
  order_outlets: z.array(outletOrderLazyDecoder),
})

export const orderDecoder = z.object({
  id: z.number(),
  created_at: z.number(),
  full_cost: z.number(),
  delivery_cost: z.number(),
  name: z.string().nullable(),
  phone: z.string(),
  address: z.string().default('нет адреса'),
  discriminator: z.string().nullable().default(null),
  distanceFromMarketToCustomer: z.number().nullable(),
  eat_place: eatPlaceSchema,
  eats_id: z.string().nullable(),
  ready_time: z.number(),
  ready_quickly: z.number(),
  cutlery: z.number(),
  note: z.string().nullable(),
  table: z.string().nullable(),
  ready_status: orderStatusSchema,
  car_number: z.string().nullable(),
  market_street_title: z.string().default('улица не выбрана'),
  delivery_street: z.string().nullable(),
  delivery_building: z.string().nullable(),
  delivery_apartment: z.string().nullable(),
  yandexDeliveryType: z.string().nullable(),
  externalDelivery: externalDeliveryDecoder.nullable().default(null),
  sberCourierHandoutCode: z.string().nullable().default(null),
  sberCourierReturnCode: z.string().nullable().default(null),
  order_outlets: z.array(outletOrderDecoder),
})

export const totalDecoder = z.object({
  waiting: z.number().default(0),
  cooking: z.number().default(0),
  ready: z.number().default(0),
  issued_courier: z.number().default(0),
  received: z.number().default(0),
  refused: z.number().default(0),
  waiting_on_counter: z.number().default(0),
})

export const ordersDecoder = z.object({
  orders: z.array(orderLazyDecoder),
  total: totalDecoder,
})
